// src/private/src/pages/app/reports/DataHealthPage.jsx
import * as React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
  Skeleton,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  AlertTitle,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HelpOutline as UnknownIcon,
  TrendingDown as TrendingDownIcon,
  PlayArrow as PlayArrowIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  ArrowForward as ArrowForwardIcon,
  DoneAll as AckIcon,
  Snooze as SnoozeIcon,
  AutoFixHigh as FixIcon,
  Psychology as CauseIcon,
  Build as RunbookIcon,
  ManageSearch as DetailsIcon,
  FilterAltOutlined as FilterIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { RouterLink } from '@src/components/core/link';
import api from '@src/api/clients';
import {
  fetchDailyPulseByClient,
  fetchClientSummaryByDate,
  triggerDailyPulseBackfill,
} from '@src/api';

dayjs.extend(relativeTime);

// --- Constants ---

const STATUS_CONFIG = {
  ok: { label: 'Healthy', color: 'success', Icon: CheckCircleIcon },
  warning: { label: 'Warning', color: 'warning', Icon: WarningIcon },
  anomaly: { label: 'Anomaly', color: 'error', Icon: TrendingDownIcon },
  missing: { label: 'Missing', color: 'error', Icon: ErrorIcon },
  unknown: { label: 'Unknown', color: 'default', Icon: UnknownIcon },
};

const TASK_STATUS_COLOR = {
  succeeded: 'success',
  failed: 'error',
  running: 'info',
  missing: 'default',
  present: 'success',
};

const MAPPING_TYPE_FROM_TASK = {
  email_mapping: 'Email',
  process_email_mapping: 'Email',
  sftp_mappings: 'SFTP',
  process_sftp_mappings: 'SFTP',
  api_mappings: 'API',
  process_api_mappings: 'API',
  google_sheet_mappings: 'GSheet',
  process_google_sheet_mappings: 'GSheet',
};

const VOLUME_DROP_THRESHOLD = 0.5; // flag if yesterday < 50% of 7d avg
const AUTO_REFRESH_MS = 5 * 60 * 1000; // 5 minutes

// --- Helper functions ---

const isTaskFresh = (task) => {
  if (!task || task.status === 'missing') return false;
  if (task.name === 'backend_data') {
    if (!task.max_created_date) return false;
    const maxDate = dayjs(task.max_created_date);
    if (!maxDate.isValid()) return Boolean(task.fresh);
    return dayjs().startOf('day').diff(maxDate.startOf('day'), 'day') <= 1;
  }
  if (task.finished_at) {
    const finished = dayjs(task.finished_at);
    if (finished.isValid()) return dayjs().diff(finished, 'hour', true) <= 24;
  }
  return Boolean(task.fresh);
};

const isTaskHealthy = (task) => {
  if (!task || task.status === 'missing') return false;
  if (task.name === 'backend_data') return isTaskFresh(task);
  return String(task.status).toLowerCase() === 'succeeded' && isTaskFresh(task);
};

const getBackendDataDate = (health) => {
  const tasks = Array.isArray(health?.tasks) ? health.tasks : [];
  const bd = tasks.find((t) => t.name === 'backend_data');
  return bd?.max_created_date || null;
};

const getMappingTypes = (health) => {
  const tasks = Array.isArray(health?.tasks) ? health.tasks : [];
  const types = new Set();
  tasks.forEach((t) => {
    const name = (t.name || '').replace(/\s/g, '_');
    Object.keys(MAPPING_TYPE_FROM_TASK).forEach((key) => {
      if (name.includes(key) || name === key) types.add(MAPPING_TYPE_FROM_TASK[key]);
    });
  });
  return [...types];
};

const deriveClientHealth = (row, anomalies) => {
  const health = row?.ingestion_health;
  const clientId = row?.client_id;
  const anom = anomalies?.[clientId];

  if (!health) return { status: 'unknown', issues: [], stalestAge: null, daysStalePipeline: 0 };
  if (!health.has_mapping) return { status: 'missing', issues: ['No data mappings configured'], stalestAge: null, daysStalePipeline: 0 };

  const tasks = Array.isArray(health.tasks) ? health.tasks : [];
  const issues = [];
  let stalestAge = null;

  for (const task of tasks) {
    if (!isTaskHealthy(task)) {
      const label = task?.name || 'Unknown';
      const statusText = task?.status === 'missing' ? 'never ran' : (task?.status || 'unknown');
      issues.push(`${label}: ${statusText}`);
    }
    const finishedAt = task?.finished_at ? dayjs(task.finished_at) : null;
    const maxDate = task?.max_created_date ? dayjs(task.max_created_date) : null;
    const ref = finishedAt?.isValid() ? finishedAt : maxDate?.isValid() ? maxDate : null;
    if (ref) {
      const age = dayjs().diff(ref, 'minute');
      if (stalestAge === null || age > stalestAge) stalestAge = age;
    }
  }

  const daysStalePipeline = stalestAge !== null ? Math.max(0, Math.floor(stalestAge / 1440) - 0) : 0;

  // Backend-data feed health — the "why" behind a stale feed (no new report /
  // arrived empty / errored), read from the lead-flow loop so it matches the
  // page's alerts and Slack.
  if (health.feed_alert?.reason) {
    issues.push(health.feed_alert.reason);
  }

  // Check for anomalies
  if (anom?.leadDrop || anom?.spendDrop) {
    const anomIssues = [];
    if (anom.leadDrop) anomIssues.push(`Leads dropped ${Math.round((1 - anom.leadRatio) * 100)}% vs 7d avg`);
    if (anom.spendDrop) anomIssues.push(`Spend dropped ${Math.round((1 - anom.spendRatio) * 100)}% vs 7d avg`);
    issues.push(...anomIssues);
  }

  let status = 'ok';
  if (anom?.leadDrop || anom?.spendDrop) status = 'anomaly';
  else if (issues.length > 0) status = 'warning';

  return { status, issues, stalestAge, daysStalePipeline };
};

const formatAge = (minutes) => {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
};

const formatDate = (d) => d.toISOString().split('T')[0];

const buildAnomalyLookup = (dailyRows) => {
  // Group by client_id → array of { date, total_spend_sum, total_leads_sum }
  const byClient = {};
  (Array.isArray(dailyRows) ? dailyRows : []).forEach((row) => {
    const id = row.client_id;
    if (!id) return;
    if (!byClient[id]) byClient[id] = [];
    byClient[id].push({
      date: row.date,
      spend: Number(row.total_spend_sum) || 0,
      leads: Number(row.total_leads_sum) || 0,
    });
  });

  const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  const lookup = {};

  Object.entries(byClient).forEach(([clientId, days]) => {
    days.sort((a, b) => (a.date > b.date ? 1 : -1));
    const yesterdayRow = days.find((d) => d.date === yesterday);
    // Use all days except yesterday for the baseline
    const baselineDays = days.filter((d) => d.date !== yesterday);

    if (!yesterdayRow || baselineDays.length < 2) {
      lookup[clientId] = { leadDrop: false, spendDrop: false, leadRatio: 1, spendRatio: 1, yesterdayLeads: null, avgLeads: null, yesterdaySpend: null, avgSpend: null };
      return;
    }

    const avgLeads = baselineDays.reduce((s, d) => s + d.leads, 0) / baselineDays.length;
    const avgSpend = baselineDays.reduce((s, d) => s + d.spend, 0) / baselineDays.length;

    const leadRatio = avgLeads > 0 ? yesterdayRow.leads / avgLeads : 1;
    const spendRatio = avgSpend > 0 ? yesterdayRow.spend / avgSpend : 1;

    lookup[clientId] = {
      leadDrop: avgLeads >= 1 && leadRatio < VOLUME_DROP_THRESHOLD,
      spendDrop: avgSpend >= 5 && spendRatio < VOLUME_DROP_THRESHOLD,
      leadRatio,
      spendRatio,
      yesterdayLeads: yesterdayRow.leads,
      avgLeads: Math.round(avgLeads * 10) / 10,
      yesterdaySpend: Math.round(yesterdayRow.spend),
      avgSpend: Math.round(avgSpend),
    };
  });

  return lookup;
};

// --- Alerts Panel ---

// --- Live alerts: severity, labels, and deep-links ---
// Each open anomaly the loops raise can be linked straight to the place in the
// app where you'd actually investigate it — so reviewing an alert is one click,
// not a hunt through Django admin.

const SEVERITY_META = {
  critical: { color: 'error', label: 'Critical' },
  high: { color: 'error', label: 'High' },
  medium: { color: 'warning', label: 'Medium' },
  low: { color: 'info', label: 'Low' },
};

const SEV_ORDER = { critical: 3, high: 2, medium: 1, low: 0 };
const _maxSeverity = (a, b) => ((SEV_ORDER[b] || 0) > (SEV_ORDER[a] || 0) ? b : a);

const ALERT_KIND_LABEL = {
  delivery_dead: 'Delivery dead',
  delivery_stuck: 'Delivery stuck',
  delivery_rejected: 'Delivery rejected',
  ingestion_drop: 'Ingestion drop',
  spend_stall: 'Spend stalled',
  fb_account_disabled: 'FB account stopped',
  out_of_area: 'Out of area',
  duplicate_spike: 'Duplicate spike',
  budget_pacing: 'Budget pacing',
  backend_feed_stale: 'Backend feed stale',
  backend_recon_gap: 'Backend recon gap',
  apply_failed: 'Apply failed',
  apply_partial: 'Apply partial',
  change_errors: 'Change errors',
  value_rule_cap: 'Value-rule cap',
  live_overspend: 'Live overspend',
  segment_runaway: 'Segment runaway',
  rollup_mismatch: 'Rollup mismatch',
  derived_drift: 'Derived drift',
  budget_archival: 'Budget archival',
};

const kindLabel = (k) => ALERT_KIND_LABEL[k] || (k || '').replace(/_/g, ' ');

// Map an anomaly to the most useful place to investigate it. Lead-level alerts
// land on the EXACT leads (via the leads `delivery` filter); optimizer alerts
// open the run; budget alerts open Budget Center; reporting drift opens the
// client dashboard where those numbers live.
function buildAlertLink(anomaly, client) {
  // System-scoped anomalies (no client) have no per-client page to deep-link to.
  if (!client || client.client_id == null) return null;
  const cid = client.client_id;
  const slug = client.client_slug || cid;
  const m = anomaly.metrics || {};
  switch (anomaly.kind) {
    case 'delivery_dead':
      return { href: `/leads?client_id=${cid}&delivery=dead`, label: 'View dead-lettered leads' };
    case 'delivery_rejected':
      return { href: `/leads?client_id=${cid}&delivery=rejected`, label: 'View rejected leads' };
    case 'delivery_stuck':
      return { href: `/leads?client_id=${cid}&delivery=undelivered`, label: 'View stuck leads' };
    case 'ingestion_drop':
    case 'duplicate_spike':
    case 'out_of_area':
    case 'backend_feed_stale':
    case 'backend_recon_gap':
      return { href: `/leads?client_id=${cid}`, label: 'View leads' };
    case 'budget_pacing':
    case 'budget_archival':
    case 'spend_stall':
      return { href: `/budget-center?client_id=${cid}`, label: 'Open Budget Center' };
    case 'fb_account_disabled':
      return { href: `/budget-center?client_id=${cid}`, label: 'Check budget & billing' };
    case 'apply_failed':
    case 'apply_partial':
    case 'change_errors': {
      const run = m.run_id;
      return {
        href: `/facebook/budget-optimizer?client_id=${cid}&tab=runs${run ? `&run=${run}` : ''}`,
        label: run ? `Open optimizer run #${run}` : 'Open optimizer',
      };
    }
    case 'value_rule_cap':
    case 'live_overspend':
    case 'segment_runaway':
      return { href: `/facebook/budget-optimizer?client_id=${cid}&tab=runs`, label: 'Open optimizer' };
    case 'rollup_mismatch':
    case 'derived_drift':
      return { href: `/clients/${slug}/dashboard`, label: 'Open client dashboard' };
    default:
      return { href: `/clients/${slug}/dashboard`, label: 'Open client' };
  }
}

// Short "how to fix" per kind — encodes the tribal knowledge so anyone can
// triage, not just whoever wrote the loop.
const RUNBOOK = {
  delivery_dead: 'Leads hit the webhook give-up limit. Confirm the client endpoint is up, then re-deliver from the lead detail. Repeating = endpoint/credentials broken.',
  delivery_rejected: "Endpoint accepted the post but the CRM didn't confirm. Verify the success keyword + payload mapping.",
  delivery_stuck: 'Webhook is still retrying. Clears itself once the endpoint is back; if not, fix the endpoint.',
  ingestion_drop: 'Leads fell while spend HELD (we ruled out a no-spend/paced-down dip), so suspect a tracking/form break or a CPL spike — not just slower delivery.',
  spend_stall: '$0 spend while a live budget still has headroom and we have NOT paused them. Almost always a failed FB card, a disabled ad account, or stopped delivery — check the ad account billing + status in Ads Manager.',
  fb_account_disabled: "Facebook stopped the ad account (disabled / unsettled / grace). If it's payment-related, fix billing in Ads Manager. No delivery until resolved, regardless of budget.",
  out_of_area: "Spend is landing outside the service area. Check geo/ZIP targeting now.",
  duplicate_spike: 'Duplicate rate jumped. Look for a double-firing source or a re-posted feed.',
  budget_pacing: 'Active budgets are off pace. Open Budget Center to adjust, or let the optimizer rebalance.',
  backend_feed_stale: 'The backend report stopped landing. Check the email/SFTP/API mapping freshness.',
  backend_recon_gap: 'Accepted leads are missing from the backend export. Check the mapping + dedupe.',
  apply_failed: 'Optimizer apply failed entirely — budgets/value rules are NOT live. Open the run and re-apply.',
  apply_partial: 'Some campaign changes failed — only partially live. Open the run; the failed-change log shows which.',
  change_errors: "Per-campaign change errors on an applied run. Open the run's failed-change log.",
  value_rule_cap: "At Facebook's 6-rule-sets-per-account cap. Pool value rules by vertical on the shared account.",
  live_overspend: "Live FB daily budgets exceed the client's allowed daily. Could be an apply OR a manual Ads Manager edit.",
  segment_runaway: "A budget segment's MTD spend is far over pace. Check that segment's campaigns.",
  rollup_mismatch: "Breakdown summaries don't reconcile to the client total. Recompute that day.",
  derived_drift: 'A derived field (e.g. CPL) disagrees with raw spend/leads. Recompute that day.',
  budget_archival: 'Stale "active" budgets are inflating totals. Complete the old months.',
};

// Money-at-risk chip, where the loop's metrics carry it — so the expensive
// alerts (overspend) read as expensive at a glance.
function dollarsAtRisk(anomaly) {
  const m = anomaly.metrics || {};
  if (anomaly.kind === 'live_overspend' && m.live_total_daily) {
    return `$${Math.round(m.live_total_daily).toLocaleString()}/day live`;
  }
  if (anomaly.kind === 'segment_runaway' && m.mtd_spend) {
    return `$${Math.round(m.mtd_spend).toLocaleString()} MTD`;
  }
  return null;
}

// New vs chronic, from first_detected_at + occurrences.
function ageInfo(anomaly) {
  const first = anomaly.first_detected_at ? dayjs(anomaly.first_detected_at) : null;
  const occ = anomaly.occurrences || 1;
  const isNew = first ? dayjs().diff(first, 'hour') < 24 : false;
  if (isNew && occ <= 1) return { isNew: true, label: 'new' };
  if (first) return { isNew: false, label: `${occ}× since ${first.format('MMM D')}` };
  return { isNew: false, label: `${occ}×` };
}

// One open anomaly: severity-colored, with the loop's own detail + AI cause +
// suggested fix + a runbook, age/$-at-risk badges, deep-link, and triage actions.
function AlertItem({ anomaly, client, onAck, onSnooze, onFix, onDetails, showClient = false, busy = false }) {
  const [expanded, setExpanded] = React.useState(false);
  const [snoozeAnchor, setSnoozeAnchor] = React.useState(null);
  const sev = SEVERITY_META[anomaly.severity] || SEVERITY_META.medium;
  const link = buildAlertLink(anomaly, client);
  const detail = anomaly.detail || anomaly.title || '';
  const isLong = detail.length > 160;
  const cause = anomaly.ai_hypothesis;
  const fix = anomaly.remediation_detail;
  const runbook = RUNBOOK[anomaly.kind];
  const age = ageInfo(anomaly);
  const risk = dollarsAtRisk(anomaly);
  const isAck = anomaly.status === 'acknowledged';
  const snoozedActive = anomaly.snoozed_until && dayjs(anomaly.snoozed_until).isAfter(dayjs());

  const doSnooze = (hours) => { setSnoozeAnchor(null); onSnooze(anomaly, hours); };

  return (
    <Box
      sx={(theme) => ({
        py: 1,
        px: 1.25,
        borderLeft: `3px solid ${theme.palette[sev.color]?.main || theme.palette.grey[400]}`,
        '&:not(:last-of-type)': { borderBottom: `1px solid ${theme.palette.divider}` },
      })}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.25 }}>
        {showClient ? (
          <Typography variant="caption" fontWeight={700}>{client.client_name}</Typography>
        ) : null}
        <Chip
          label={kindLabel(anomaly.kind)}
          size="small"
          color={sev.color}
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600 }}
        />
        {age.isNew
          ? <Chip label="new" size="small" color="info" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
          : <Typography variant="caption" color="text.secondary">{age.label}</Typography>}
        {risk ? <Chip label={risk} size="small" color="error" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} /> : null}
        {isAck ? <Chip label="acknowledged" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} /> : null}
        {snoozedActive ? <Chip label={`snoozed → ${dayjs(anomaly.snoozed_until).format('MMM D')}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} /> : null}
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={!expanded && isLong ? {
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        } : undefined}
      >
        {detail}
      </Typography>

      {cause ? (
        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
          <CauseIcon sx={{ fontSize: 14, color: 'info.main', mt: '2px' }} />
          <Typography variant="caption" color="text.secondary"><strong>Likely cause:</strong> {cause}</Typography>
        </Stack>
      ) : null}
      {fix ? (
        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.25 }}>
          <FixIcon sx={{ fontSize: 14, color: 'success.main', mt: '2px' }} />
          <Typography variant="caption" color="text.secondary"><strong>Suggested fix:</strong> {fix}</Typography>
        </Stack>
      ) : null}
      {expanded && runbook ? (
        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.25 }}>
          <RunbookIcon sx={{ fontSize: 14, color: 'text.disabled', mt: '2px' }} />
          <Typography variant="caption" color="text.secondary"><strong>Runbook:</strong> {runbook}</Typography>
        </Stack>
      ) : null}
      {(isLong || runbook) ? (
        <Button size="small" variant="text" onClick={() => setExpanded((v) => !v)} sx={{ p: 0, minWidth: 0, fontSize: '0.7rem' }}>
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      ) : null}

      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        {link ? (
          <Button component={RouterLink} href={link.href} size="small" variant="outlined" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none' }}>
            {link.label}
          </Button>
        ) : null}
        <Button size="small" variant="text" startIcon={<DetailsIcon sx={{ fontSize: 16 }} />} onClick={() => onDetails(anomaly, client)} sx={{ textTransform: 'none' }}>
          Details
        </Button>
        <Button size="small" variant="text" startIcon={<AckIcon sx={{ fontSize: 16 }} />} onClick={() => onAck(anomaly, !isAck)} disabled={busy} sx={{ textTransform: 'none' }}>
          {isAck ? 'Unack' : 'Ack'}
        </Button>
        <Button size="small" variant="text" startIcon={<SnoozeIcon sx={{ fontSize: 16 }} />} onClick={(e) => setSnoozeAnchor(e.currentTarget)} disabled={busy} sx={{ textTransform: 'none' }}>
          Snooze
        </Button>
        <Menu anchorEl={snoozeAnchor} open={Boolean(snoozeAnchor)} onClose={() => setSnoozeAnchor(null)}>
          <MenuItem onClick={() => doSnooze(24)}>1 day</MenuItem>
          <MenuItem onClick={() => doSnooze(72)}>3 days</MenuItem>
          <MenuItem onClick={() => doSnooze(168)}>1 week</MenuItem>
          {snoozedActive ? <MenuItem onClick={() => doSnooze(0)}>Clear snooze</MenuItem> : null}
        </Menu>
        {anomaly.can_remediate ? (
          <Button size="small" variant="contained" color="warning" startIcon={<FixIcon sx={{ fontSize: 16 }} />} onClick={() => onFix(anomaly, client)} disabled={busy} sx={{ textTransform: 'none' }}>
            Fix
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}

// Slide-over with the full picture for one alert: cause / fix / runbook, the raw
// metrics, and — for delivery alerts — the exact underlying leads pulled inline.
function AlertDetailDrawer({ entry, onClose, onAck, onSnooze, onFix, busy }) {
  const [leads, setLeads] = React.useState(null);
  const [leadsLoading, setLeadsLoading] = React.useState(false);
  const anomaly = entry?.anomaly;
  const client = entry?.client;

  const DELIVERY_FILTER = { delivery_dead: 'dead', delivery_rejected: 'rejected', delivery_stuck: 'undelivered' };

  React.useEffect(() => {
    if (!anomaly) return;
    const deliveryKind = DELIVERY_FILTER[anomaly.kind];
    if (!deliveryKind) { setLeads(null); return; }
    setLeadsLoading(true);
    api.request({
      url: 'dashboard/leads/',
      method: 'GET',
      params: {
        client: client.client_id,
        delivery: deliveryKind,
        fields: 'id,first_name,last_name,phone,failed_webhook_attempts,success_post,post_response,date_created',
        page_size: 15,
      },
    })
      .then((resp) => setLeads(resp?.data?.results || []))
      .catch(() => setLeads([]))
      .finally(() => setLeadsLoading(false));
  }, [anomaly, client]); // eslint-disable-line react-hooks/exhaustive-deps

  const metrics = anomaly?.metrics || {};
  const metricRows = Object.entries(metrics).filter(([, v]) => typeof v !== 'object' || v === null);

  return (
    <Drawer anchor="right" open={Boolean(entry)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 460 } } }}>
      {anomaly ? (
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">{client.client_name}</Typography>
            <Chip label={SEVERITY_META[anomaly.severity]?.label || anomaly.severity} size="small" color={SEVERITY_META[anomaly.severity]?.color || 'default'} />
          </Stack>
          <Chip label={kindLabel(anomaly.kind)} size="small" color={SEVERITY_META[anomaly.severity]?.color} variant="outlined" sx={{ mb: 1.5, fontWeight: 600 }} />

          <Typography variant="body2" sx={{ mb: 1.5 }}>{anomaly.detail}</Typography>

          {anomaly.ai_hypothesis ? (
            <Alert severity="info" icon={<CauseIcon fontSize="inherit" />} sx={{ mb: 1, py: 0.25 }}>
              <Typography variant="caption"><strong>Likely cause:</strong> {anomaly.ai_hypothesis}</Typography>
            </Alert>
          ) : null}
          {anomaly.remediation_detail ? (
            <Alert severity="success" icon={<FixIcon fontSize="inherit" />} sx={{ mb: 1, py: 0.25 }}>
              <Typography variant="caption"><strong>Suggested fix:</strong> {anomaly.remediation_detail}</Typography>
            </Alert>
          ) : null}
          {RUNBOOK[anomaly.kind] ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              <RunbookIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5 }} />
              {RUNBOOK[anomaly.kind]}
            </Typography>
          ) : null}

          {metricRows.length ? (
            <>
              <Typography variant="overline" color="text.secondary">Metrics</Typography>
              <Box component="table" sx={{ width: '100%', mb: 1.5, '& td': { py: 0.25, fontSize: '0.75rem', verticalAlign: 'top' } }}>
                <tbody>
                  {metricRows.map(([k, v]) => (
                    <tr key={k}>
                      <td style={{ color: '#888', paddingRight: 8, whiteSpace: 'nowrap' }}>{k.replace(/_/g, ' ')}</td>
                      <td><strong>{String(v)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </>
          ) : null}

          {DELIVERY_FILTER[anomaly.kind] ? (
            <>
              <Typography variant="overline" color="text.secondary">Affected leads</Typography>
              {leadsLoading ? (
                <CircularProgress size={18} sx={{ display: 'block', my: 1 }} />
              ) : leads && leads.length ? (
                <List dense disablePadding sx={{ mb: 1.5 }}>
                  {leads.map((lead) => (
                    <ListItem
                      key={lead.id}
                      disableGutters
                      secondaryAction={
                        <Button component={RouterLink} href={`/leads/${lead.id}`} size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}>Open</Button>
                      }
                    >
                      <ListItemText
                        primary={`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.phone || lead.id}
                        secondary={`${lead.failed_webhook_attempts ?? 0} failed attempts · ${lead.post_response ? String(lead.post_response).slice(0, 60) : 'no response'}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>No matching leads found.</Typography>
              )}
            </>
          ) : null}

          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button size="small" startIcon={<AckIcon sx={{ fontSize: 16 }} />} onClick={() => onAck(anomaly, anomaly.status !== 'acknowledged')} disabled={busy}>
              {anomaly.status === 'acknowledged' ? 'Unacknowledge' : 'Acknowledge'}
            </Button>
            <Button size="small" startIcon={<SnoozeIcon sx={{ fontSize: 16 }} />} onClick={() => onSnooze(anomaly, 72)} disabled={busy}>Snooze 3d</Button>
            {anomaly.can_remediate ? (
              <Button size="small" variant="contained" color="warning" startIcon={<FixIcon sx={{ fontSize: 16 }} />} onClick={() => onFix(anomaly, client)} disabled={busy}>Fix</Button>
            ) : null}
          </Stack>
        </Box>
      ) : null}
    </Drawer>
  );
}

// Shared triage state + actions for both the per-client worklist and the
// system-health banner: one POST then refetch. Bulk variants fan the same calls
// across a whole group (#3). `refetch` is whatever reloads that surface's rows.
function useAlertTriage(refetch) {
  const [busyUid, setBusyUid] = React.useState(null);
  const [drawerEntry, setDrawerEntry] = React.useState(null); // { anomaly, client }
  const [confirmFix, setConfirmFix] = React.useState(null); // { anomaly, client }

  const triage = React.useCallback(async (url, payload) => {
    setBusyUid(payload.uid);
    try {
      await api.request({ url, method: 'POST', payload });
      await refetch();
      setDrawerEntry(null);
    } catch (err) {
      console.error('Triage error', err);
    } finally {
      setBusyUid(null);
    }
  }, [refetch]);

  const triageBulk = React.useCallback(async (url, anomalies, extra = {}) => {
    if (!anomalies?.length) return;
    setBusyUid('bulk');
    try {
      await Promise.all(anomalies.map((a) =>
        api.request({ url, method: 'POST', payload: { uid: a.uid, source: a.source, ...extra } })));
      await refetch();
    } catch (err) {
      console.error('Bulk triage error', err);
    } finally {
      setBusyUid(null);
    }
  }, [refetch]);

  const handleAck = (a, ack) => triage('dashboard/alerts/acknowledge/', { uid: a.uid, source: a.source, ack });
  const handleSnooze = (a, hours) => triage('dashboard/alerts/snooze/', { uid: a.uid, source: a.source, hours });
  const handleFixConfirmed = (a) => triage('dashboard/alerts/remediate/', { uid: a.uid, source: a.source });
  const handleOpenDetails = (a, client) => setDrawerEntry({ anomaly: a, client });
  const handleFix = (a, client) => setConfirmFix({ anomaly: a, client });
  const ackAll = (anoms) => triageBulk('dashboard/alerts/acknowledge/', anoms, { ack: true });
  const snoozeAll = (anoms, hours) => triageBulk('dashboard/alerts/snooze/', anoms, { hours });

  return {
    busyUid, drawerEntry, setDrawerEntry, confirmFix, setConfirmFix,
    actions: { onAck: handleAck, onSnooze: handleSnooze, onFix: handleFix, onDetails: handleOpenDetails },
    handleAck, handleSnooze, handleFix, handleFixConfirmed, handleOpenDetails, ackAll, snoozeAll,
  };
}

// Confirm dialog for the loop's approved remediation (shared by worklist + banner).
function ConfirmFixDialog({ confirmFix, onClose, onConfirm }) {
  return (
    <Dialog open={Boolean(confirmFix)} onClose={onClose}>
      <DialogTitle>Run the suggested fix?</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          {confirmFix ? (
            <>
              This dispatches the loop&apos;s approved remediation for{' '}
              <strong>{confirmFix.client?.client_name || 'this system check'}</strong> — {kindLabel(confirmFix.anomaly.kind)}.
              {confirmFix.anomaly.remediation_detail ? (
                <Box component="span" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  {confirmFix.anomaly.remediation_detail}
                </Box>
              ) : null}
              <Box component="span" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                Runs the same approved remediation as the Slack approve button, recorded under your account.
              </Box>
            </>
          ) : null}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" startIcon={<FixIcon />} onClick={onConfirm}>
          Run fix
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Ack-all / Snooze-all for a whole group (#3). "Ack all" only touches rows that
// aren't already acknowledged; "Snooze all" offers a duration.
function BulkTriage({ anomalies, onAckAll, onSnoozeAll, busy }) {
  const [anchor, setAnchor] = React.useState(null);
  const actionable = (anomalies || []).filter((a) => a.status !== 'acknowledged');
  return (
    <>
      <Button
        size="small" variant="text" startIcon={<AckIcon sx={{ fontSize: 15 }} />}
        disabled={busy || !actionable.length} onClick={() => onAckAll(actionable)}
        sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0 }}
      >
        Ack all
      </Button>
      <Button
        size="small" variant="text" startIcon={<SnoozeIcon sx={{ fontSize: 15 }} />}
        disabled={busy || !(anomalies || []).length} onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0 }}
      >
        Snooze all
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {[['1 day', 24], ['3 days', 72], ['1 week', 168]].map(([label, hrs]) => (
          <MenuItem key={hrs} onClick={() => { setAnchor(null); onSnoozeAll(anomalies, hrs); }}>{label}</MenuItem>
        ))}
      </Menu>
    </>
  );
}

function AlertsPanel() {
  const [activeTab, setActiveTab] = React.useState('live'); // 'live' | 'thresholds' | 'history'
  const [alertResults, setAlertResults] = React.useState(null);
  const [thresholds, setThresholds] = React.useState(null);
  const [history, setHistory] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [editingClient, setEditingClient] = React.useState(null);
  const [editValues, setEditValues] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  // Worklist controls (#2/#3/#4)
  const [groupMode, setGroupMode] = React.useState('client'); // 'client' | 'issue'
  const [sevFilter, setSevFilter] = React.useState('all'); // 'all' | 'critical_high'
  const [newOnly, setNewOnly] = React.useState(false);
  const [showAcknowledged, setShowAcknowledged] = React.useState(false);

  const fetchAlerts = React.useCallback(() => {
    setLoading(true);
    api.request({
      url: 'dashboard/alerts/evaluate-all/',
      method: 'GET',
      params: { view: showAcknowledged ? 'all' : 'open' },
    })
      .then((resp) => setAlertResults(resp?.data || null))
      .catch((err) => console.error('Alert eval error', err))
      .finally(() => setLoading(false));
  }, [showAcknowledged]);

  const fetchThresholds = React.useCallback(() => {
    setLoading(true);
    api.request({ url: 'dashboard/alerts/thresholds/', method: 'GET' })
      .then((resp) => setThresholds(resp?.data || null))
      .catch((err) => console.error('Threshold fetch error', err))
      .finally(() => setLoading(false));
  }, []);

  const fetchHistory = React.useCallback(() => {
    setLoading(true);
    api.request({ url: 'dashboard/alerts/history/', method: 'GET', params: { limit: 30 } })
      .then((resp) => setHistory(resp?.data || null))
      .catch((err) => console.error('History fetch error', err))
      .finally(() => setLoading(false));
  }, []);

  // Live refetches whenever the tab is active or the acknowledged toggle flips
  // (fetchAlerts identity changes with showAcknowledged).
  React.useEffect(() => {
    if (activeTab === 'live') fetchAlerts();
  }, [activeTab, fetchAlerts]);
  React.useEffect(() => {
    if (activeTab === 'thresholds' && !thresholds) fetchThresholds();
    if (activeTab === 'history' && !history) fetchHistory();
  }, [activeTab, thresholds, history, fetchThresholds, fetchHistory]);

  // Shared triage state + actions (same hook powers the system-health banner).
  const {
    busyUid, drawerEntry, setDrawerEntry, confirmFix, setConfirmFix,
    actions: alertActions, handleAck, handleSnooze, handleFix, handleFixConfirmed,
    ackAll, snoozeAll,
  } = useAlertTriage(fetchAlerts);

  const startEditing = (client) => {
    setEditingClient(client.client_id);
    setEditValues({
      alert_lead_sample_size: client.alert_lead_sample_size,
      alert_out_of_area_threshold: client.alert_out_of_area_threshold,
      alert_success_post_threshold: client.alert_success_post_threshold,
      alert_webhook_freshness_minutes: client.alert_webhook_freshness_minutes,
    });
  };

  const saveThreshold = () => {
    setSaving(true);
    api.request({
      url: 'dashboard/alerts/thresholds/',
      method: 'PATCH',
      payload: { client_id: editingClient, ...editValues },
    })
      .then(() => {
        setEditingClient(null);
        setThresholds(null); // force refetch
        fetchThresholds();
      })
      .catch((err) => console.error('Save error', err))
      .finally(() => setSaving(false));
  };

  const triggeredClients = alertResults?.clients?.filter((c) => c.any_triggered) || [];

  // #5 Severity counts across the whole open set (drives the click-to-filter
  // strip), independent of the current filter so the totals stay stable.
  const sevCounts = React.useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    (alertResults?.clients || []).forEach((c) =>
      (c.anomalies || []).forEach((a) => { if (counts[a.severity] != null) counts[a.severity] += 1; }));
    return counts;
  }, [alertResults]);

  // Apply the worklist filters (severity / new-only) to each client's anomalies,
  // then drop clients left with nothing. sevFilter is 'all' | 'critical_high' |
  // one of the four severities (set by the strip chips).
  const matchFilters = (a) => {
    if (sevFilter !== 'all') {
      if (sevFilter === 'critical_high') {
        if (!['critical', 'high'].includes(a.severity)) return false;
      } else if (a.severity !== sevFilter) return false;
    }
    if (newOnly && !ageInfo(a).isNew) return false;
    return true;
  };
  const visibleClients = triggeredClients
    .map((c) => ({ ...c, anomalies: (c.anomalies || []).filter(matchFilters) }))
    .filter((c) => c.anomalies.length > 0);

  // #3 "By issue": collapse the SAME root cause across clients into one group
  // (e.g. 8 clients all hitting the value-rule cap), worst-first.
  const issueGroups = (() => {
    const map = {};
    visibleClients.forEach((c) => c.anomalies.forEach((a) => {
      if (!map[a.kind]) map[a.kind] = { kind: a.kind, items: [], severity: a.severity };
      map[a.kind].items.push({ anomaly: a, client: c });
      map[a.kind].severity = _maxSeverity(map[a.kind].severity, a.severity);
    }));
    return Object.values(map).sort((x, y) => {
      const s = (SEV_ORDER[y.severity] || 0) - (SEV_ORDER[x.severity] || 0);
      return s !== 0 ? s : y.items.length - x.items.length;
    });
  })();

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="Alerts"
        subheader="Live alert evaluation, thresholds, and history"
        action={
          <Stack direction="row" spacing={0.5}>
            {['live', 'thresholds', 'history'].map((tab) => (
              <Button
                key={tab}
                size="small"
                variant={activeTab === tab ? 'contained' : 'outlined'}
                onClick={() => setActiveTab(tab)}
                sx={{ textTransform: 'capitalize', minWidth: 80 }}
              >
                {tab === 'live' ? `Live (${alertResults?.triggered_count ?? '?'})` : tab}
              </Button>
            ))}
          </Stack>
        }
      />
      <Divider />
      <CardContent>
        {loading && <CircularProgress size={20} sx={{ display: 'block', mx: 'auto', my: 2 }} />}

        {/* Live tab */}
        {activeTab === 'live' && !loading && alertResults && (
          <Box>
            <Stack direction="row" spacing={1.5} mb={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">
                {alertResults.total_clients} evaluated, <strong>{visibleClients.length}</strong> shown
                {alertResults.hidden_count ? ` · ${alertResults.hidden_count} hidden` : ''}
              </Typography>
              {/* Reads persisted rows; detection itself re-runs on the Beat
                  schedule, so this is a refresh, not a re-run. */}
              <Button size="small" variant="text" onClick={fetchAlerts} startIcon={<Refresh sx={{ fontSize: 16 }} />}>Refresh</Button>
              <Box sx={{ flexGrow: 1 }} />
              <ToggleButtonGroup size="small" value={groupMode} exclusive onChange={(_, v) => v && setGroupMode(v)}>
                <ToggleButton value="client" sx={{ textTransform: 'none', py: 0.25 }}>By client</ToggleButton>
                <ToggleButton value="issue" sx={{ textTransform: 'none', py: 0.25 }}>By issue</ToggleButton>
              </ToggleButtonGroup>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel><FilterIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.25 }} />Severity</InputLabel>
                <Select value={sevFilter} label="⏷ Severity" onChange={(e) => setSevFilter(e.target.value)}>
                  <MenuItem value="all">All severities</MenuItem>
                  <MenuItem value="critical_high">Critical &amp; High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Switch size="small" checked={newOnly} onChange={(e) => setNewOnly(e.target.checked)} />}
                label={<Typography variant="caption">New only</Typography>}
              />
              <FormControlLabel
                control={<Switch size="small" checked={showAcknowledged} onChange={(e) => setShowAcknowledged(e.target.checked)} />}
                label={<Typography variant="caption">Show acknowledged</Typography>}
              />
            </Stack>

            {/* #5 Severity strip — counts across the open set; click to filter. */}
            {(sevCounts.critical + sevCounts.high + sevCounts.medium + sevCounts.low) > 0 ? (
              <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap alignItems="center">
                {['critical', 'high', 'medium', 'low'].map((s) => (
                  sevCounts[s] ? (
                    <Chip
                      key={s}
                      size="small"
                      label={`${SEVERITY_META[s].label} ${sevCounts[s]}`}
                      color={SEVERITY_META[s].color}
                      variant={sevFilter === s ? 'filled' : 'outlined'}
                      onClick={() => setSevFilter(sevFilter === s ? 'all' : s)}
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }}
                    />
                  ) : null
                ))}
                {sevFilter !== 'all' ? (
                  <Button size="small" variant="text" onClick={() => setSevFilter('all')} sx={{ fontSize: '0.7rem', minWidth: 0 }}>Clear filter</Button>
                ) : null}
              </Stack>
            ) : null}

            {visibleClients.length === 0 ? (
              <Typography variant="body2" color={triggeredClients.length === 0 ? 'success.main' : 'text.secondary'} py={2}>
                {triggeredClients.length === 0
                  ? (alertResults.hidden_count
                      ? `All clear — ${alertResults.hidden_count} alert${alertResults.hidden_count === 1 ? '' : 's'} acknowledged or snoozed. Toggle “Show acknowledged” to view.`
                      : 'All clients are healthy — no alerts triggered.')
                  : 'No alerts match the current filters.'}
              </Typography>
            ) : groupMode === 'client' ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: { xs: 150, sm: 230 } }}>Client</TableCell>
                    <TableCell>Alerts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleClients.map((c) => {
                    const sev = SEVERITY_META[c.severity] || SEVERITY_META.medium;
                    return (
                      <TableRow
                        key={c.client_id}
                        sx={(theme) => ({
                          verticalAlign: 'top',
                          backgroundColor: alpha(theme.palette[sev.color]?.light || theme.palette.error.light, 0.05),
                        })}
                      >
                        <TableCell>
                          <Typography
                            component={RouterLink}
                            href={`/clients/${c.client_slug || c.client_id}/dashboard`}
                            variant="subtitle2"
                            sx={{ textDecoration: 'none', fontWeight: 600, color: 'text.primary', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {c.client_name}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                            <Chip label={sev.label} size="small" color={sev.color} sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
                            <Typography variant="caption" color="text.secondary">
                              {c.anomalies.length} alert{c.anomalies.length === 1 ? '' : 's'}
                            </Typography>
                          </Stack>
                          {c.anomalies.length > 1 ? (
                            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ mt: 0.25 }}>
                              <BulkTriage anomalies={c.anomalies} onAckAll={ackAll} onSnoozeAll={snoozeAll} busy={busyUid === 'bulk'} />
                            </Stack>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ p: 0 }}>
                          {c.anomalies.map((a) => (
                            <AlertItem key={a.uid} anomaly={a} client={c} busy={busyUid === a.uid} {...alertActions} />
                          ))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              // By-issue: one section per root cause across all clients.
              issueGroups.map((g) => {
                const sev = SEVERITY_META[g.severity] || SEVERITY_META.medium;
                return (
                  <Box key={g.kind} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                      <Chip label={kindLabel(g.kind)} size="small" color={sev.color} sx={{ fontWeight: 700 }} />
                      <Typography variant="subtitle2">{g.items.length} client{g.items.length === 1 ? '' : 's'}</Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <BulkTriage anomalies={g.items.map((i) => i.anomaly)} onAckAll={ackAll} onSnoozeAll={snoozeAll} busy={busyUid === 'bulk'} />
                    </Stack>
                    {RUNBOOK[g.kind] ? (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        <RunbookIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5 }} />{RUNBOOK[g.kind]}
                      </Typography>
                    ) : null}
                    <Paper variant="outlined">
                      {g.items.map(({ anomaly, client }) => (
                        <AlertItem key={anomaly.uid} anomaly={anomaly} client={client} showClient busy={busyUid === anomaly.uid} {...alertActions} />
                      ))}
                    </Paper>
                  </Box>
                );
              })
            )}

            <AlertDetailDrawer
              entry={drawerEntry}
              onClose={() => setDrawerEntry(null)}
              onAck={handleAck}
              onSnooze={handleSnooze}
              onFix={handleFix}
              busy={busyUid === drawerEntry?.anomaly?.uid}
            />

            <ConfirmFixDialog
              confirmFix={confirmFix}
              onClose={() => setConfirmFix(null)}
              onConfirm={() => { handleFixConfirmed(confirmFix.anomaly); setConfirmFix(null); }}
            />
          </Box>
        )}

        {/* Thresholds tab */}
        {activeTab === 'thresholds' && !loading && thresholds && (
          <Box>
            <GlobalThresholds />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Sample Size</TableCell>
                  <TableCell>OOA Threshold</TableCell>
                  <TableCell>Success Post %</TableCell>
                  <TableCell>Webhook Fresh (min)</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(thresholds.clients || []).map((c) => {
                  const isEditing = editingClient === c.client_id;
                  return (
                    <TableRow key={c.client_id}>
                      <TableCell><Typography variant="body2" fontWeight={500}>{c.client_name}</Typography></TableCell>
                      {isEditing ? (
                        <React.Fragment>
                          <TableCell><TextField size="small" type="number" value={editValues.alert_lead_sample_size} onChange={(e) => setEditValues((v) => ({ ...v, alert_lead_sample_size: e.target.value }))} sx={{ width: 80 }} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={editValues.alert_out_of_area_threshold} onChange={(e) => setEditValues((v) => ({ ...v, alert_out_of_area_threshold: e.target.value }))} sx={{ width: 80 }} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={editValues.alert_success_post_threshold} onChange={(e) => setEditValues((v) => ({ ...v, alert_success_post_threshold: e.target.value }))} sx={{ width: 80 }} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={editValues.alert_webhook_freshness_minutes} onChange={(e) => setEditValues((v) => ({ ...v, alert_webhook_freshness_minutes: e.target.value }))} sx={{ width: 80 }} /></TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Button size="small" variant="contained" onClick={saveThreshold} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                              <Button size="small" variant="text" onClick={() => setEditingClient(null)}>Cancel</Button>
                            </Stack>
                          </TableCell>
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <TableCell>{c.alert_lead_sample_size}</TableCell>
                          <TableCell>{c.alert_out_of_area_threshold}%</TableCell>
                          <TableCell>{c.alert_success_post_threshold}%</TableCell>
                          <TableCell>{c.alert_webhook_freshness_minutes}m</TableCell>
                          <TableCell>
                            <Button size="small" variant="text" onClick={() => startEditing(c)}>Edit</Button>
                          </TableCell>
                        </React.Fragment>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* History tab */}
        {activeTab === 'history' && !loading && history && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {history.count} recent alert task runs
            </Typography>
            {history.entries?.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={2}>No alert task history found.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Task</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(history.entries || []).map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Typography variant="body2">{entry.name?.replace(/_/g, ' ')}</Typography>
                        {entry.client_name && <Typography variant="caption" color="text.secondary">{entry.client_name}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.status}
                          size="small"
                          color={entry.status === 'succeeded' ? 'success' : entry.status === 'failed' ? 'error' : 'default'}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{entry.created ? dayjs(entry.created).fromNow() : '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{entry.duration_seconds ? `${Math.round(entry.duration_seconds)}s` : '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        {entry.messages?.map((msg, i) => (
                          <Typography key={i} variant="caption" display="block" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {msg}
                          </Typography>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// --- Editable loop thresholds ---
// The numbers the loops actually use to decide what's an alert. Editing one
// changes detection in BOTH Slack and this page (they read the same loop).
const GLOBAL_THRESHOLD_FIELDS = [
  { key: 'volume_drop_ratio', label: 'Volume drop (× baseline)', step: '0.05' },
  { key: 'min_baseline_leads', label: 'Min baseline leads', step: '1' },
  { key: 'duplicate_spike_points', label: 'Duplicate spike (pts)', step: '1' },
  { key: 'out_of_area_pct', label: 'Out-of-area (%)', step: '1' },
  { key: 'reject_rate_pct', label: 'Reject rate (%)', step: '1' },
  { key: 'budget_overspend_pct', label: 'Budget overspend (%)', step: '5' },
  { key: 'budget_underspend_pct', label: 'Budget underspend (%)', step: '5' },
];

function GlobalThresholds() {
  const [values, setValues] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    api.request({ url: 'dashboard/alerts/global-thresholds/', method: 'GET' })
      .then((resp) => { if (active) setValues(resp?.data?.thresholds || null); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const save = () => {
    setSaving(true);
    api.request({ url: 'dashboard/alerts/global-thresholds/', method: 'PATCH', payload: values })
      .then((resp) => { setValues(resp?.data?.thresholds || values); setDirty(false); })
      .catch((err) => console.error('Save thresholds error', err))
      .finally(() => setSaving(false));
  };

  if (!values) return null;

  return (
    <Box mb={2} p={1.5} sx={(theme) => ({ backgroundColor: alpha(theme.palette.info.light, 0.08), borderRadius: 1 })}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" fontWeight={600}>
          Loop thresholds — what triggers an alert (applies to all clients; drives Slack + this page)
        </Typography>
        <Button
          size="small"
          variant="contained"
          disabled={!dirty || saving}
          onClick={save}
          startIcon={saving ? <CircularProgress size={12} /> : null}
        >
          Save
        </Button>
      </Stack>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {GLOBAL_THRESHOLD_FIELDS.map((f) => (
          <TextField
            key={f.key}
            label={f.label}
            type="number"
            size="small"
            value={values[f.key] ?? ''}
            onChange={(e) => { setValues((v) => ({ ...v, [f.key]: e.target.value })); setDirty(true); }}
            inputProps={{ step: f.step, min: 0 }}
            sx={{ width: 170 }}
          />
        ))}
      </Stack>
    </Box>
  );
}

// --- System-health banner ---
// System-scoped anomalies from the system-health loop (no client FK) — pipeline
// /job problems that aren't about one client. Read straight from the loop so it
// matches Slack. Renders nothing when all clear.
// System-scoped health issues (no client FK) — pipeline/job health. Uses the
// SAME rich AlertItem + triage as the per-client worklist (#1), so cause / fix /
// runbook + Ack / Snooze / Fix all work here too; the backend already serves the
// full payload and supports remediation for system_health.
function SystemHealthBanner() {
  const [items, setItems] = React.useState([]);

  const fetchStatus = React.useCallback(
    () => api.request({ url: 'dashboard/alerts/system-status/', method: 'GET' })
      .then((resp) => setItems(resp?.data?.anomalies || []))
      .catch(() => {}),
    [],
  );
  React.useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const {
    busyUid, drawerEntry, setDrawerEntry, confirmFix, setConfirmFix,
    actions, handleAck, handleSnooze, handleFix, handleFixConfirmed,
  } = useAlertTriage(fetchStatus);

  if (!items.length) return null;

  const worst = items.some((i) => i.severity === 'critical' || i.severity === 'high') ? 'error' : 'warning';
  // Synthetic client so the generic AlertItem/drawer render (deep-links are
  // guarded off for a null client id).
  const sysClient = { client_id: null, client_name: 'System', client_slug: '' };

  return (
    <Box sx={{ mb: 2 }}>
      <Alert severity={worst} icon={<WarningIcon fontSize="inherit" />} sx={{ mb: 1 }}>
        <AlertTitle>System health — {items.length} active issue{items.length !== 1 ? 's' : ''}</AlertTitle>
        Pipeline &amp; job health that isn’t tied to one client. Triage or fix below.
      </Alert>
      <Paper variant="outlined">
        {items.map((a) => (
          <AlertItem key={a.uid} anomaly={a} client={sysClient} busy={busyUid === a.uid} {...actions} />
        ))}
      </Paper>

      <AlertDetailDrawer
        entry={drawerEntry}
        onClose={() => setDrawerEntry(null)}
        onAck={handleAck}
        onSnooze={handleSnooze}
        onFix={handleFix}
        busy={busyUid === drawerEntry?.anomaly?.uid}
      />
      <ConfirmFixDialog
        confirmFix={confirmFix}
        onClose={() => setConfirmFix(null)}
        onConfirm={() => { handleFixConfirmed(confirmFix.anomaly); setConfirmFix(null); }}
      />
    </Box>
  );
}

// --- Main Component ---

export default function DataHealthPage() {
  const [clients, setClients] = React.useState([]);
  const [anomalies, setAnomalies] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [orderBy, setOrderBy] = React.useState('staleness');
  const [orderDir, setOrderDir] = React.useState('desc');
  const [clientSlugLookup, setClientSlugLookup] = React.useState({});
  const [parentLookup, setParentLookup] = React.useState({});
  const [statusLookup, setStatusLookup] = React.useState({});
  const [autoRefresh, setAutoRefresh] = React.useState(false);
  const [backfillLoading, setBackfillLoading] = React.useState({});
  const [expandedParents, setExpandedParents] = React.useState(new Set());
  const [groupByParent, setGroupByParent] = React.useState(false);
  const [showInactive, setShowInactive] = React.useState(false);
  const [lastRefreshed, setLastRefreshed] = React.useState(null);

  const fetchData = React.useCallback(() => {
    setIsLoading(true);

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    Promise.all([
      // Current month client data with ingestion health.
      // Default to active clients only — paused/off clients (0 leads, $0 spend,
      // 100+ days stale) are noise here, not data-health problems.
      fetchDailyPulseByClient({
        date_after: formatDate(monthStart),
        date_before: formatDate(monthEnd),
        page_size: 200,
        ...(showInactive ? {} : { active: true }),
      }),
      // Last 7 days per-client per-day data for anomaly detection
      fetchClientSummaryByDate({
        date_after: formatDate(sevenDaysAgo),
        date_before: formatDate(yesterday),
        page_size: 2000,
      }),
      // Client metadata
      api.request({
        url: 'dashboard/clients/',
        method: 'GET',
        params: { fields: 'id,slug,name,parent_company,parent_company_name,status', page_size: 500 },
      }),
    ])
      .then(([pulseResp, dailyResp, clientsResp]) => {
        const rows = Array.isArray(pulseResp?.data?.results)
          ? pulseResp.data.results
          : Array.isArray(pulseResp?.data) ? pulseResp.data : [];

        const dailyRows = Array.isArray(dailyResp?.data?.results)
          ? dailyResp.data.results
          : Array.isArray(dailyResp?.data) ? dailyResp.data : [];

        const clientList = Array.isArray(clientsResp?.data?.results)
          ? clientsResp.data.results
          : Array.isArray(clientsResp?.data) ? clientsResp.data : [];

        const slugMap = {};
        const parentMap = {};
        const statusMap = {};
        clientList.forEach((c) => {
          if (c?.id) {
            if (c.slug) slugMap[String(c.id)] = c.slug;
            if (c.parent_company_name) parentMap[String(c.id)] = c.parent_company_name;
            if (c.status) statusMap[String(c.id)] = c.status;
          }
        });
        setClientSlugLookup(slugMap);
        setParentLookup(parentMap);
        setStatusLookup(statusMap);

        const anomalyLookup = buildAnomalyLookup(dailyRows);
        setAnomalies(anomalyLookup);

        const enriched = rows
          .filter((r) => r?.client__name)
          .map((row) => ({
            ...row,
            _health: deriveClientHealth(row, anomalyLookup),
            _backendDate: getBackendDataDate(row.ingestion_health),
            _mappings: getMappingTypes(row.ingestion_health),
            _anomaly: anomalyLookup[row.client_id] || null,
          }));

        setClients(enriched);
        setLastRefreshed(new Date());
      })
      .catch((err) => {
        console.error('DataHealthPage fetch error', err);
        setClients([]);
      })
      .finally(() => setIsLoading(false));
  }, [showInactive]);

  React.useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh
  React.useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrderDir(field === 'client' ? 'asc' : 'desc');
    }
  };

  const handleBackfill = React.useCallback((clientId) => {
    setBackfillLoading((prev) => ({ ...prev, [clientId]: true }));
    const client = clients.find((c) => c.client_id === clientId);
    const staleDays = client?._health?.daysStalePipeline || 0;
    const backfillDays = Math.max(3, Math.min(staleDays + 1, 14));
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - backfillDays);
    triggerDailyPulseBackfill({
      client_id: clientId,
      start_date: formatDate(start),
      end_date: formatDate(now),
    })
      .then(() => {
        setBackfillLoading((prev) => ({ ...prev, [clientId]: 'done' }));
        setTimeout(() => setBackfillLoading((prev) => ({ ...prev, [clientId]: false })), 3000);
      })
      .catch(() => {
        setBackfillLoading((prev) => ({ ...prev, [clientId]: 'error' }));
        setTimeout(() => setBackfillLoading((prev) => ({ ...prev, [clientId]: false })), 3000);
      });
  }, []);

  const filteredClients = React.useMemo(() => {
    let list = clients;
    if (statusFilter !== 'all') {
      list = list.filter((c) => c._health.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((c) => {
        const name = (c.client__name || '').toLowerCase();
        const parent = (parentLookup[String(c.client_id)] || '').toLowerCase();
        return name.includes(q) || parent.includes(q);
      });
    }
    const dir = orderDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (orderBy === 'client') return dir * (a.client__name || '').localeCompare(b.client__name || '');
      if (orderBy === 'status') {
        const order = { anomaly: 0, missing: 1, warning: 2, unknown: 3, ok: 4 };
        return dir * ((order[a._health.status] ?? 3) - (order[b._health.status] ?? 3));
      }
      if (orderBy === 'staleness') return dir * ((a._health.stalestAge ?? -1) - (b._health.stalestAge ?? -1));
      if (orderBy === 'leads') {
        const aRatio = a._anomaly?.leadRatio ?? 1;
        const bRatio = b._anomaly?.leadRatio ?? 1;
        return dir * (aRatio - bRatio);
      }
      if (orderBy === 'spend') {
        const aRatio = a._anomaly?.spendRatio ?? 1;
        const bRatio = b._anomaly?.spendRatio ?? 1;
        return dir * (aRatio - bRatio);
      }
      if (orderBy === 'issues') return dir * (a._health.issues.length - b._health.issues.length);
      return 0;
    });
  }, [clients, statusFilter, searchQuery, orderBy, orderDir, parentLookup]);

  const statusCounts = React.useMemo(() => {
    const counts = { all: 0, ok: 0, warning: 0, missing: 0, anomaly: 0 };
    clients.forEach((c) => { counts.all++; const s = c._health.status; if (s in counts) counts[s]++; });
    return counts;
  }, [clients]);

  // Group by parent company
  const groupedClients = React.useMemo(() => {
    if (!groupByParent) return null;
    const groups = {};
    filteredClients.forEach((c) => {
      const parent = parentLookup[String(c.client_id)] || 'No Parent Company';
      if (!groups[parent]) groups[parent] = [];
      groups[parent].push(c);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [groupByParent, filteredClients, parentLookup]);

  const buildClientPath = (clientId) => {
    const slug = clientSlugLookup[String(clientId)];
    return `/clients/${slug || clientId}/dashboard`;
  };

  const renderSortHeader = (label, field, props = {}) => (
    <TableCell {...props}>
      <TableSortLabel active={orderBy === field} direction={orderBy === field ? orderDir : 'asc'} onClick={() => handleSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const renderAnomalyCell = (value, avg, ratio, isDrop, label) => {
    if (value === null || value === undefined) return <Typography variant="body2" color="text.secondary">-</Typography>;
    if (!isDrop) {
      return <Typography variant="body2">{label === 'spend' ? `$${value.toLocaleString()}` : value}</Typography>;
    }
    const pctDrop = Math.round((1 - ratio) * 100);
    return (
      <Tooltip arrow title={`Yesterday: ${label === 'spend' ? '$' : ''}${value.toLocaleString()} vs 7d avg: ${label === 'spend' ? '$' : ''}${avg.toLocaleString()}`}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
          <Typography variant="body2" color="error.main" fontWeight={600}>
            {label === 'spend' ? `$${value.toLocaleString()}` : value}
          </Typography>
          <Typography variant="caption" color="error.main">-{pctDrop}%</Typography>
        </Stack>
      </Tooltip>
    );
  };

  const renderRow = (row) => {
    const h = row._health;
    const cfg = STATUS_CONFIG[h.status] || STATUS_CONFIG.unknown;
    const StatusIcon = cfg.Icon;
    const tasks = Array.isArray(row.ingestion_health?.tasks) ? row.ingestion_health.tasks : [];
    const anom = row._anomaly;
    const bfState = backfillLoading[row.client_id];
    const parent = parentLookup[String(row.client_id)];

    return (
      <TableRow
        key={row.uuid || row.client_id}
        sx={(theme) => ({
          ...(h.status === 'anomaly' && { backgroundColor: alpha(theme.palette.error.light, 0.08) }),
          ...(h.status === 'warning' && { backgroundColor: alpha(theme.palette.warning.light, 0.06) }),
          ...(h.status === 'missing' && { backgroundColor: alpha(theme.palette.error.light, 0.04) }),
        })}
      >
        {/* Client */}
        <TableCell>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography component={RouterLink} href={buildClientPath(row.client_id)} variant="subtitle2" sx={{ textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
              {row.client__name}
            </Typography>
            {statusLookup[String(row.client_id)] === 'active_new' ? (
              <Chip label="NEW" size="small" color="info" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} title="Newly launched — monitored more closely" />
            ) : null}
          </Stack>
          {!groupByParent && parent ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.1 }}>{parent}</Typography>
          ) : null}
        </TableCell>

        {/* Status */}
        <TableCell>
          <Chip icon={<StatusIcon sx={{ fontSize: 16 }} />} label={cfg.label} color={cfg.color} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.75rem' }} />
        </TableCell>

        {/* Last Data */}
        <TableCell>
          {row._backendDate ? (
            <Tooltip arrow title={`Backend data last seen: ${row._backendDate}`}>
              <Typography variant="body2" color={dayjs().diff(dayjs(row._backendDate), 'day') > 1 ? 'error.main' : 'text.primary'} fontWeight={dayjs().diff(dayjs(row._backendDate), 'day') > 1 ? 600 : 400}>
                {dayjs(row._backendDate).format('MM/DD')}
              </Typography>
            </Tooltip>
          ) : <Typography variant="body2" color="text.secondary">-</Typography>}
        </TableCell>

        {/* Days Stale */}
        <TableCell>
          <Typography variant="body2" color={h.daysStalePipeline > 1 ? 'error.main' : h.daysStalePipeline === 1 ? 'warning.main' : 'text.primary'} fontWeight={h.daysStalePipeline > 0 ? 600 : 400}>
            {h.stalestAge !== null ? (h.daysStalePipeline > 0 ? `${h.daysStalePipeline}d` : formatAge(h.stalestAge)) : '-'}
          </Typography>
        </TableCell>

        {/* Leads (anomaly) */}
        <TableCell>
          {renderAnomalyCell(anom?.yesterdayLeads, anom?.avgLeads, anom?.leadRatio, anom?.leadDrop, 'leads')}
        </TableCell>

        {/* Spend (anomaly) */}
        <TableCell>
          {renderAnomalyCell(anom?.yesterdaySpend, anom?.avgSpend, anom?.spendRatio, anom?.spendDrop, 'spend')}
        </TableCell>

        {/* Issues */}
        <TableCell>
          {h.issues.length > 0 ? (
            <Tooltip arrow title={h.issues.join('\n')} slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}>
              <Typography variant="body2" color="warning.main" fontWeight={600} sx={{ cursor: 'default' }}>{h.issues.length}</Typography>
            </Tooltip>
          ) : <Typography variant="body2" color="success.main">0</Typography>}
        </TableCell>

        {/* Mappings */}
        <TableCell>
          <Stack direction="row" spacing={0.25}>
            {row._mappings.map((m) => (
              <Chip key={m} label={m} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
            ))}
            {row._mappings.length === 0 && <Typography variant="caption" color="text.secondary">-</Typography>}
          </Stack>
        </TableCell>

        {/* Tasks */}
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {tasks.map((task, idx) => {
              const healthy = isTaskHealthy(task);
              const chipColor = healthy ? 'success' : task.status === 'missing' ? 'default' : TASK_STATUS_COLOR[task.status] || 'warning';
              const tooltipLines = [`Status: ${task.status || 'unknown'}`];
              if (task.runtime_seconds) tooltipLines.push(`Runtime: ${Math.round(task.runtime_seconds)}s`);
              if (task.finished_at) tooltipLines.push(`Finished: ${dayjs(task.finished_at).fromNow()}`);
              if (task.max_created_date) tooltipLines.push(`Latest data: ${task.max_created_date}`);
              if (!isTaskFresh(task)) tooltipLines.push('Stale (>24h)');
              return (
                <Tooltip key={`${task.name}-${idx}`} arrow title={tooltipLines.join('\n')} slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}>
                  <Chip label={task.name?.replace(/_/g, ' ').replace('process ', '')} size="small" color={chipColor} variant={healthy ? 'outlined' : 'filled'} sx={{ fontSize: '0.65rem', height: 20 }} />
                </Tooltip>
              );
            })}
          </Stack>
        </TableCell>

        {/* Actions */}
        <TableCell>
          <Tooltip title={`Backfill last ${Math.max(3, Math.min((h.daysStalePipeline || 0) + 1, 14))} days`}>
            <span>
              <IconButton
                size="small"
                color={bfState === 'done' ? 'success' : bfState === 'error' ? 'error' : 'primary'}
                disabled={bfState === true}
                onClick={() => handleBackfill(row.client_id)}
              >
                {bfState === true ? <CircularProgress size={16} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  const colCount = 10;

  const renderParentGroup = ([parentName, groupRows]) => {
    const isExpanded = expandedParents.has(parentName);
    const worstStatus = groupRows.reduce((worst, c) => {
      const order = { anomaly: 0, missing: 1, warning: 2, unknown: 3, ok: 4 };
      return (order[c._health.status] ?? 3) < (order[worst] ?? 3) ? c._health.status : worst;
    }, 'ok');
    const cfg = STATUS_CONFIG[worstStatus] || STATUS_CONFIG.ok;

    return (
      <React.Fragment key={parentName}>
        <TableRow
          sx={(theme) => ({ backgroundColor: alpha(theme.palette.primary.main, 0.04), cursor: 'pointer' })}
          onClick={() => setExpandedParents((prev) => {
            const next = new Set(prev);
            if (next.has(parentName)) next.delete(parentName); else next.add(parentName);
            return next;
          })}
        >
          <TableCell colSpan={colCount}>
            <Stack direction="row" spacing={1} alignItems="center">
              {isExpanded ? <KeyboardArrowDown sx={{ fontSize: 18 }} /> : <KeyboardArrowRight sx={{ fontSize: 18 }} />}
              <Typography variant="subtitle2" fontWeight={700}>{parentName}</Typography>
              <Chip label={`${groupRows.length} clients`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              <Chip label={cfg.label} size="small" color={cfg.color} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            </Stack>
          </TableCell>
        </TableRow>
        {isExpanded && groupRows.map(renderRow)}
      </React.Fragment>
    );
  };

  return (
    <Box p={3}>
      <SystemHealthBanner />
      <Card>
        <CardHeader
          title="Data Health Monitor"
          subheader="Pipeline status, data freshness, and anomaly detection across all clients"
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
                label={<Typography variant="caption">Show inactive</Typography>}
                sx={{ mr: 0 }}
              />
              <FormControlLabel
                control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
                label={<Typography variant="caption">Auto-refresh</Typography>}
                sx={{ mr: 0 }}
              />
              <Button variant="outlined" size="small" startIcon={isLoading ? <CircularProgress size={14} /> : <Refresh fontSize="small" />} onClick={fetchData} disabled={isLoading}>
                Refresh
              </Button>
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          {/* Summary chips */}
          <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap" alignItems="center">
            {[
              { key: 'all', label: `All (${statusCounts.all})` },
              { key: 'anomaly', label: `Anomalies (${statusCounts.anomaly})`, color: 'error' },
              { key: 'warning', label: `Warnings (${statusCounts.warning})`, color: 'warning' },
              { key: 'missing', label: `Missing (${statusCounts.missing})`, color: 'error' },
              { key: 'ok', label: `Healthy (${statusCounts.ok})`, color: 'success' },
            ].map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                color={statusFilter === chip.key ? (chip.color || 'primary') : 'default'}
                variant={statusFilter === chip.key ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter(chip.key)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            {lastRefreshed && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                Updated {dayjs(lastRefreshed).format('h:mm A')}
              </Typography>
            )}
          </Stack>

          {/* Controls */}
          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
              }}
            />
            <FormControlLabel
              control={<Switch size="small" checked={groupByParent} onChange={(e) => setGroupByParent(e.target.checked)} />}
              label={<Typography variant="caption">Group by parent</Typography>}
            />
            <Typography variant="caption" color="text.secondary">
              {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>

          <TableContainer component={Paper} sx={{ maxHeight: '68vh', borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {renderSortHeader('Client', 'client', { sx: { minWidth: 160 } })}
                  {renderSortHeader('Status', 'status', { sx: { minWidth: 90 } })}
                  <TableCell sx={{ minWidth: 70 }}>Last Data</TableCell>
                  {renderSortHeader('Stale', 'staleness', { sx: { minWidth: 60 } })}
                  {renderSortHeader('Leads (yd)', 'leads', { sx: { minWidth: 90 } })}
                  {renderSortHeader('Spend (yd)', 'spend', { sx: { minWidth: 100 } })}
                  {renderSortHeader('Issues', 'issues', { sx: { minWidth: 55 } })}
                  <TableCell sx={{ minWidth: 100 }}>Mappings</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>Tasks</TableCell>
                  <TableCell sx={{ minWidth: 50 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={`skel-${i}`}>
                      {Array.from({ length: colCount }).map((_, j) => (
                        <TableCell key={j}><Skeleton width={j === 0 ? 130 : j === 8 ? 200 : 60} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        {searchQuery ? 'No clients match your search.' : 'No clients found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : groupByParent && groupedClients ? (
                  groupedClients.map(renderParentGroup)
                ) : (
                  filteredClients.map(renderRow)
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Live Alerts Panel */}
          <AlertsPanel />
        </CardContent>
      </Card>
    </Box>
  );
}
